import { Injectable, Inject } from '@angular/core';
import { BehaviorSubject, Observable, throwError, of, combineLatest, defer, from } from 'rxjs';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { catchError, tap, take, finalize, map, filter, switchMap, shareReplay } from 'rxjs/operators';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { SessionService } from '../session/session.service';
import { TokenData, TokenResponse } from './auth.models';
import { API_BASE_URL, MitIdClient } from '@shared/http-clients/http-clients';
import { NgxPermissionsService } from 'ngx-permissions';
import { Router } from '@angular/router';
import { NotificationService } from '../notifications/notification.service';

export const AUTH_KEY = 'AUTH_DATA';
export const tokenEndpoint = '/api/Token';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _isAuthenticated$ = new BehaviorSubject<boolean>(false);
  readonly isAuthenticated$ = this._isAuthenticated$.asObservable();

  private _hasUserAcceptedTermsConditions$ = new BehaviorSubject<boolean>(false);
  readonly hasUserAcceptedTermsConditions$ = this._hasUserAcceptedTermsConditions$.asObservable();

  private _isLoadingLogin$ = new BehaviorSubject<boolean>(false);
  readonly isLoadingLogin$ = this._isLoadingLogin$.asObservable();

  private _tokenData$ = new BehaviorSubject<TokenData>(undefined);
  readonly tokenData$ = this._tokenData$.asObservable();
  private refreshRequest$: Observable<TokenResponse>;
  private sessionVersion = 0;

  get tokenData() {
    return this._tokenData$.getValue();
  }

  get tokenUrl(): string {
    return (this.baseUrl + tokenEndpoint).replace(/[?&]$/, '');
  }

  constructor(
    private http: HttpClient,
    private storageService: LocalStorageService,
    private permissionService: NgxPermissionsService,
    private session: SessionService,
    private router: Router,
    private notificationsService: NotificationService,
    private mitIdClient: MitIdClient,
    @Inject(API_BASE_URL) private baseUrl: string
  ) { }

  init(): Promise<boolean> {
    // Remove credentials and personal data left by older versions before restoring a session.
    this.removeLegacyStorage();
    return this.refreshToken().pipe(
      switchMap(() => this.session.fetchUserInfo()),
      map(() => {
        this._isAuthenticated$.next(true);
        return true;
      }),
      catchError(() => {
        this.clearSession();
        return of(false);
      })
    ).toPromise();
  }

  isApiUrl(url: string): boolean {
    const api = new URL(this.baseUrl || '/', window.location.origin);
    const target = new URL(url, window.location.origin);
    return target.origin === api.origin && target.pathname.startsWith(api.pathname.replace(/\/$/, '') + '/api/');
  }

  login(
    email: string,
    password: string,
    rememberMe: boolean
  ): Observable<TokenResponse> {
    this._isLoadingLogin$.next(true);

    const body = new HttpParams()
      .set('userName', encodeURIComponent(email))
      .set('password', encodeURIComponent(password))
      .set('grant_type', 'password')
      .set('client_id', 'PC-APP')
      .set('client_secret', 'PC-SECRET')
      .set('remember_me', String(rememberMe));

    // todo -> grant_type, client_id, client_secret should go to config

    const options = {
      headers: new HttpHeaders()
        .set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8')
        .set('X-Kailo-Session', '1'),
      withCredentials: true
    };

    return this.http
      .post<TokenResponse>(this.tokenUrl, body.toString(), options)
      .pipe(
        finalize(() => this._isLoadingLogin$.next(false)),
        switchMap(token => this.acceptSession(token, rememberMe))
      );
  }

  logout() {
    this.clearSession();
    this.http.post(this.tokenUrl + '/Logout', '', {
      headers: new HttpHeaders().set('X-Kailo-Session', '1')
        .set('Content-Type', 'application/x-www-form-urlencoded'),
      withCredentials: true
    }).subscribe({ error: () => this.notificationsService.error('Unable to revoke the server session. Please try signing out again.') });
  }

  private removeLegacyStorage() {
    ['KAILO-AUTH_DATA', 'KAILO-USER_INFO', 'KAILO-pbi_token_expiration'].forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  }

  private clearSession() {
    this.sessionVersion++;
    this.removeLegacyStorage();
    sessionStorage.removeItem('KAILO-SESSION');
    this._tokenData$.next(undefined);
    this.permissionService.flushPermissions();
    this.session.clearData();
    this._isAuthenticated$.next(false);
    this._hasUserAcceptedTermsConditions$.next(false);
  }

  refreshToken(_previous?: TokenData): Observable<TokenResponse> {
    if (!this.refreshRequest$) {
      const version = this.sessionVersion;
      const body = new HttpParams().set('grant_type', 'refresh_token');
      const send = () => this.http.post<TokenResponse>(this.tokenUrl, body.toString(), {
        headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8')
          .set('X-Kailo-Session', '1'),
        withCredentials: true
      });
      this.refreshRequest$ = defer(() => {
        // Serialize rotations across tabs too, so legitimate requests do not trigger replay revocation.
        const locks = typeof navigator === 'undefined' ? undefined : (navigator as any).locks;
        return locks ? from(locks.request('kailo-refresh:' + this.baseUrl, () => {
          if (version !== this.sessionVersion) throw new Error('Session changed during refresh');
          return send().toPromise();
        })) as Observable<TokenResponse> : send();
      }).pipe(
        tap(token => {
          if (version !== this.sessionVersion) throw new Error('Session changed during refresh');
          this.setTokenData(token as TokenData);
        }),
        finalize(() => this.refreshRequest$ = undefined),
        shareReplay(1)
      );
    }
    return this.refreshRequest$;
  }

  setTermsConditions(status: boolean) {
    const loginInfo = this.tokenData;
    if (loginInfo) this._tokenData$.next({ ...loginInfo, hasRequiredUserConsent: String(status) });
    this._hasUserAcceptedTermsConditions$.next(status);
  }

  setTokenData(token: TokenData, rememberMe = this.tokenData?.rememberMe || false) {
    if (!token?.access_token) throw new Error('Invalid login response');
    // Explicitly exclude refresh tokens and profile data, even from legacy API responses.
    const { refresh_token, userInfo, ...safeToken } = token;
    this._tokenData$.next({ ...safeToken, rememberMe } as TokenData);
    this._hasUserAcceptedTermsConditions$.next(token.hasRequiredUserConsent === 'true');
  }

  private acceptSession(token: TokenResponse, rememberMe = false): Observable<TokenResponse> {
    this.sessionVersion++;
    this.setTokenData(token as TokenData, rememberMe);
    if (rememberMe) sessionStorage.removeItem('KAILO-SESSION');
    else sessionStorage.setItem('KAILO-SESSION', 'active');
    return this.session.fetchUserInfo().pipe(
      tap(() => this._isAuthenticated$.next(true)),
      map(() => token),
      catchError(error => {
        this.logout();
        return throwError(error);
      })
    );
  }

  openMitId() {
    this._isLoadingLogin$.next(true);
    return this.mitIdClient.openMitId()
      .pipe(finalize(() => this._isLoadingLogin$.next(false)));
  }

  authorizeMitIdUser(url: string) {
    var params = `grant_type=any&client_id=PC-APP&client_secret=PC-SECRET&url=${url}&mitid_state=${this.storageService.getItem("mitid_state")}` ;

    var xhr = new XMLHttpRequest();
    xhr.open('POST', this.tokenUrl, true);
    xhr.withCredentials = true;
    xhr.setRequestHeader('X-Kailo-Session', '1');

    xhr.setRequestHeader(
      'Content-Type',
      'application/x-www-form-urlencoded;charset=UTF-8'
    );

    xhr.onload = (e: any) => {
      if (xhr.response != null && xhr.response != '') {
        let token: any = JSON.parse(xhr.response);

        if (token.error) {
          if (token.error != '') {
            this.notificationsService.error(token.error);
            this.router.navigate(['login'])
          }
        }
        else {
          this.acceptSession(token).subscribe(() => combineLatest([
              this.isAuthenticated$,
              this.hasUserAcceptedTermsConditions$
            ]).pipe(
              filter(([isAuthenticated]) => isAuthenticated),
              take(1)
            ).subscribe(([isAuthenticated, hasUserAcceptedTermsConditions]) => {
              if (hasUserAcceptedTermsConditions) {
                this.router.navigate(['']);
              } else {
                this.router.navigate(['/terms'],{ queryParams: { codeType: btoa('BOOKINGS'), codeEntity: btoa('PATIENT') }});
              }
            }), () => this.router.navigate(['login']));
          this.storageService.removeItem("mitid_state");
        }
      }
    };

    xhr.send(params);
  }

  authorizeSocialUser(url: string, idToken: string, email: string, fullName: string, provider: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const params = new URLSearchParams({
        grant_type: 'any', client_id: 'PC-APP', provider,
        email, fullName, token_id: idToken, client_secret: 'PC-SECRET', url
      }).toString();
      const xhr = new XMLHttpRequest();
      xhr.open('POST', this.tokenUrl, true);
      xhr.withCredentials = true;
      xhr.setRequestHeader('X-Kailo-Session', '1');
      xhr.timeout = 90000;
      xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded;charset=UTF-8');
      xhr.onload = () => {
        let token: any;
        try {
          token = JSON.parse(xhr.response);
        } catch (_) {
          reject(new Error('Invalid social login response'));
          return;
        }
        if (token?.error) {
          this.notificationsService.error(token.error);
          resolve(false);
          return;
        }
        if (xhr.status < 200 || xhr.status >= 300 || !token?.access_token) {
          reject(new Error('Social login failed'));
          return;
        }
        this.acceptSession(token).subscribe(() => resolve(true), reject);
      };
      xhr.onerror = () => reject(new Error('Social login request failed'));
      xhr.ontimeout = () => reject(new Error('Social login request timed out'));
      xhr.send(params);
    });
  }
}
